// Боковая панель (Base UI Dialog): shadcn 4.21.1 (base-mira), подогнана под ui-spec §4–§5 (задача D5):
// - модальная: затемнение --scrim (.scrim, 200 / 140 мс), ловушка фокуса, Esc и щелчок мимо закрывают, фокус
//   возвращается туда, откуда открыли (Base UI);
// - выезжает из-за края: класс .sheet из motion.css — сдвиг на свою ширину за 240 мс по кривой выдвижной панели, уход
//   за 180 мс; при уменьшенном движении — только затухание 150 / 120 мс. Сдвиг 2,5 rem и переход по всем свойствам
//   из генератора убраны;
// - ширину и высоту задаёт тот, кто ставит панель (SheetPanel, меню на телефоне); текст 13/18, на телефоне — 14/20;
// - кнопка закрытия — иконка с подписью «Закрыть» для чтения с экрана;
// - поверх панели открыто окно подтверждения — панель затемнена тем же --scrim (`data-nested-dialog-open` Base UI,
//   правило `.sheet::after` в motion.css): своё затемнение у вложенного окна Base UI не рисует.
import { Dialog as SheetPrimitive } from '@base-ui/react/dialog';
import { cn } from 'cn';
import { XIcon } from 'lucide-react';
import type * as React from 'react';
import { Button } from '@/components/ui/button';

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn('scrim fixed inset-0 z-50 min-h-dvh bg-scrim', className)}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: 'right' | 'left';
  showCloseButton?: boolean;
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          'sheet fixed inset-y-0 z-50 flex h-dvh flex-col bg-popover text-base text-popover-foreground shadow-dialog outline-none',
          side === 'right' ? 'right-0 border-l border-border' : 'left-0 border-r border-border',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            aria-label="Закрыть"
            render={<Button variant="ghost" size="icon" className="absolute top-3 right-3 max-sm:top-2 max-sm:right-2" />}
          >
            <XIcon aria-hidden="true" />
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-header" className={cn('flex flex-col gap-1 px-6 pt-4 pr-14 pb-3', className)} {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-3', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title data-slot="sheet-title" className={cn('text-md font-semibold text-foreground', className)} {...props} />
  );
}

function SheetDescription({ className, ...props }: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger };
